import type { DocumentNode } from 'graphql'

import { GQLResult, LightGQLServiceHelper } from './LightGQLService.helper'

const GITLAB_STATUS = process.env.GITLAB_STATUS || null
const IS_STAND = GITLAB_STATUS === '1'

const GRAPHQL_AFISHA_PROD = 'https://graph.afisha.ru/graphql'
// const GRAPHQL_AFISHA_STAGE = 'https://graph-dev.afisha.ru/graphql'
const GQL_AFISHA_URI = GRAPHQL_AFISHA_PROD

const GRAPHQL_DAILY_PROD = 'https://daily.afisha.ru/public/api/graphql/'
const GRAPHQL_DAILY_STAGE = 'https://react-stage.daily.afisha.ru/public/api/graphql/'
const GQL_DAILY_URI = !IS_STAND ? GRAPHQL_DAILY_PROD : GRAPHQL_DAILY_STAGE

type Variables = Record<string, any>

export type QueryOptions<TVariables extends Variables, TResult = any> = {
  variables?: TVariables
  mapping?: (data: any) => TResult
  hash?: string // sha256 hardcode
  requestInit?: RequestInit
}

class LightGQLService {
  constructor(private endpoint: string) {}

  private async makeRequest<TResult, TVariables extends Variables>(
    query: DocumentNode,
    options?: QueryOptions<TVariables>,
  ): Promise<GQLResult<TResult>> {
    const { name, string: queryString, hash } = await LightGQLServiceHelper.getOperationMetainfo(query)
    const extensions = LightGQLServiceHelper.extensions({ persistedQueryHash: options?.hash || hash })

    const variables = options?.variables ?? {}

    // Сначала пробуем GET с persisted query
    const getResult = await LightGQLServiceHelper.getRequest<TResult>(
      this.endpoint,
      { operationName: name, variables, extensions },
      options.requestInit,
    )

    // Если persisted query не найден, делаем POST с полным запросом
    if (this.isPersistedQueryNotFound(getResult)) {
      return LightGQLServiceHelper.postRequest<TResult>(
        this.endpoint,
        { operationName: name, query: queryString, variables, extensions },
        options.requestInit,
      )
    }

    return getResult
  }

  private isPersistedQueryNotFound(result: GQLResult<any>): boolean {
    return 'errors' in result && result.errors?.[0]?.message === 'PersistedQueryNotFound'
  }

  async query<TResult = any, TVariables extends Variables = never>(
    query: DocumentNode,
    options?: QueryOptions<TVariables, TResult>,
  ): Promise<TResult> {
    const result = await this.makeRequest<TResult, TVariables>(query, options)

    if ('errors' in result) {
      throw new Error(result.errors[0]?.message || 'GraphQL error')
    }

    return options?.mapping ? options.mapping(result.data) : result.data
  }
}

export const LightGQL = {
  afisha: new LightGQLService(GQL_AFISHA_URI),
  daily: new LightGQLService(GQL_DAILY_URI),
}
